import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography, Stack, Button, Collapse } from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  useDeviceConfigEntries,
  useAlarmsLimits,
  useSystemConfig,
  useSystemId,
  useSolutionType,  
} from "../../../../hooks/useReduxSelectors";
import { CustomAlarms } from "../../../../actions/apiActions";
import { getAllAlarmLimits } from "../../../../ducks/alarmsLimitsDuck";

const inputSx = {
  bgcolor: "#272727",
  border: "1px solid #555",
  borderRadius: "4px",
  color: "#fff",
  fontSize: "16px",
  p: "6px 10px",
  width: "100%",
  boxSizing: "border-box",
  "&:focus": { outline: "none", borderColor: "primary.main" },
};

const RIGHT_PANEL_MIN_WIDTH = 320;
const RIGHT_PANEL_MAX_WIDTH = 900;
const RIGHT_PANEL_DEFAULT_WIDTH = 700;
const RIGHT_PANEL_WIDTH_STORAGE_KEY = "createDeleteAlarmsRightPanelWidth";

const CATEGORY_INFO = {
  Device: "Indicates a hardware-related fault with the device itself.",
  SampleCondition: "Indicates a fault in the sample handling system or its surrounding equipment (e.g. sampling line, probe, or conditioning equipment).",
};

const LIMIT_FIELDS = [
  { key: "lowAlarm", label: "Low Alarm limit", placeholder: "e.g. -10" },
  { key: "highAlarm", label: "High Alarm limit", placeholder: "e.g. 100" },
  { key: "lowWarning", label: "Low Warning limit", placeholder: "e.g. -5" },
  { key: "highWarning", label: "High Warning limit", placeholder: "e.g. 90" },
];

// Prefilled into the matching *Desc field the first time its limit gets a value; user edits are preserved afterwards.
const DEFAULT_LIMIT_DESCRIPTIONS = {
  lowAlarm: "{{valueName}} has dropped to a critical low level.",
  highAlarm: "{{valueName}} has risen to a critical high level.",
  lowWarning: "{{valueName}} has dropped below the normal operating range.",
  highWarning: "{{valueName}} has risen above the normal operating range.",
};

const CreateDeleteAlarms = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const systemId = useSystemId();
  const devices = useDeviceConfigEntries();
  const alarmsLimits = useAlarmsLimits();
  const config = useSystemConfig();
  const solutionType = useSolutionType();

  const [selectedMeasurement, setSelectedMeasurement] = useState(null); // { hwId, valueName, deviceName }
  const [formValues, setFormValues] = useState({
    alarmName: "",
    category: "",
    lowAlarm: "",
    highAlarm: "",
    lowWarning: "",
    highWarning: "",
    lowAlarmDesc: "",
    highAlarmDesc: "",
    lowWarningDesc: "",
    highWarningDesc: "",
  });
  const [descTouched, setDescTouched] = useState({}); // tracks which *Desc fields the user has manually edited
  const [saveStatus, setSaveStatus] = useState(""); // "", "saving", "success", "error"
  const [errorMsg, setErrorMsg] = useState("");

  const containerRef = useRef(null);
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    const stored = Number(localStorage.getItem(RIGHT_PANEL_WIDTH_STORAGE_KEY));
    return stored >= RIGHT_PANEL_MIN_WIDTH && stored <= RIGHT_PANEL_MAX_WIDTH
      ? stored
      : RIGHT_PANEL_DEFAULT_WIDTH;
  });

  // Dragging the divider right shrinks the form panel (grows the list panel), and vice versa.
  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = containerRef.current?.querySelector("[data-right-panel]")?.offsetWidth ?? rightPanelWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (moveEvent) => {
      const containerWidth = containerRef.current?.getBoundingClientRect().width ?? Infinity;
      const maxWidth = Math.min(RIGHT_PANEL_MAX_WIDTH, containerWidth - RIGHT_PANEL_MIN_WIDTH);
      const newWidth = Math.min(
        Math.max(startWidth - (moveEvent.clientX - startX), RIGHT_PANEL_MIN_WIDTH),
        maxWidth
      );
      setRightPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      setRightPanelWidth((w) => {
        localStorage.setItem(RIGHT_PANEL_WIDTH_STORAGE_KEY, String(w));
        return w;
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  }, [rightPanelWidth]);

  useEffect(() => {
    if (systemId && config) {
      dispatch(getAllAlarmLimits({ systemId, solutionType }));
    }
  }, [systemId]);

  // Build a Set of "hwId|valueName" keys for measurements that already have alarm limits
  const existingAlarmKeys = useMemo(() => {
    const entries = alarmsLimits?.entries ?? [];
    return new Set(entries.map((e) => `${e.hwId}|${e.valueName}`));
  }, [alarmsLimits?.entries]);

  const handleMeasurementClick = useCallback((hwId, valueName, deviceName, measurementName) => {
    setSelectedMeasurement({ hwId, valueName, deviceName, measurementName });
    setFormValues({
      alarmName: "",
      category: "",
      lowAlarm: "",
      highAlarm: "",
      lowWarning: "",
      highWarning: "",
      lowAlarmDesc: "",
      highAlarmDesc: "",
      lowWarningDesc: "",
      highWarningDesc: "",
    });
    setDescTouched({});
    setSaveStatus("");
    setErrorMsg("");
  }, []);

  const handleFieldChange = useCallback((field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Auto-fills the default description the first time a limit gets a value; leaves user edits untouched afterwards.
  const handleLimitChange = useCallback((field, value) => {
    const descField = `${field}Desc`;
    setFormValues((prev) => {
      const next = { ...prev, [field]: value };
      if (!value) {
        next[descField] = "";
      } else if (!descTouched[field] && !prev[descField]) {
        next[descField] = t(DEFAULT_LIMIT_DESCRIPTIONS[field], {
          valueName: selectedMeasurement?.measurementName ?? "",
        });
      }
      return next;
    });
  }, [descTouched, selectedMeasurement, t]);

  const handleDescChange = useCallback((field, value) => {
    setDescTouched((prev) => ({ ...prev, [field]: true }));
    setFormValues((prev) => ({ ...prev, [`${field}Desc`]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedMeasurement || !config) return;

    const { hwId, valueName } = selectedMeasurement;
    const {
      alarmName, category, lowAlarm, highAlarm, lowWarning, highWarning,
      lowAlarmDesc, highAlarmDesc, lowWarningDesc, highWarningDesc,
    } = formValues;

    if (!lowAlarm && !highAlarm && !lowWarning && !highWarning) {
      setErrorMsg(t("At least one limit (Low/High Alarm or Warning) is required"));
      return;
    }

    if (!alarmName) {
      setErrorMsg(t("Alarm name is required"));
      return;
    }

    if (!category) {
      setErrorMsg(t("Category is required"));
      return;
    }

    const toLimitNumber = (value) =>
      value ? Number(value.toString().replace(/,/g, ".")) : null;
    const toDesc = (limitValue, descValue) => (limitValue ? (descValue || null) : null);

    const payload = {
      HwId: hwId,
      AlarmName: alarmName,
      Category: category,
      ValueName: valueName,
      LowAlarm: toLimitNumber(lowAlarm),
      HighAlarm: toLimitNumber(highAlarm),
      LowWarning: toLimitNumber(lowWarning),
      HighWarning: toLimitNumber(highWarning),
      LowAlarmDesc: toDesc(lowAlarm, lowAlarmDesc),
      HighAlarmDesc: toDesc(highAlarm, highAlarmDesc),
      LowWarningDesc: toDesc(lowWarning, lowWarningDesc),
      HighWarningDesc: toDesc(highWarning, highWarningDesc),
      SiteId: config.CUSTOMER_ID,
      EdgeUnitId: config.DEVICE_ID,
    };

    setSaveStatus("saving");
    setErrorMsg("");

    try {
      await CustomAlarms.create(config.DATA_URL, payload);
      setSaveStatus("success");
      setSelectedMeasurement(null);
      dispatch(getAllAlarmLimits({ systemId, solutionType }));
    } catch (e) {
      setSaveStatus("error");
      setErrorMsg(t("Save failed. Please try again."));
    }
  }, [selectedMeasurement, formValues, config, systemId, solutionType, t, dispatch]);

  const handleDelete = useCallback(async (alarm) => {
    if (!config) return;

    if (!window.confirm(t("Are you sure you want to delete this custom alarm?"))) {
      return;
    }

    try {
      await CustomAlarms.remove(config.DATA_URL, {
        HwId: alarm.hwId,
        ValueName: alarm.valueName,
        SiteId: config.CUSTOMER_ID,
        EdgeUnitId: config.DEVICE_ID
      });
      dispatch(getAllAlarmLimits({ systemId, solutionType }));
    } catch (e) {
      alert(t("Delete failed. Please try again."));
    }
  }, [config, systemId, solutionType, t, dispatch]);

  return (
    <Box ref={containerRef} sx={{ display: "flex", flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>

      {/* Left: list of devices with unbound measurements & custom alarms */}
      <Box sx={{ flex: "1 1 0", minWidth: 0, overflow: "auto", p: 2 }}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1.5, pb: 0.5, borderBottom: "1px solid #333" }}>
              {t("Create New Custom Alarm")}
            </Typography>
            <Stack spacing={2}>
              {devices?.map((device) => {
                const unbound = device.measurements?.filter(
                  (m) => !existingAlarmKeys.has(`${device.hwId}|${m.valueName}`)
                );
                if (!unbound || unbound.length === 0) return null;
                return (
                  <Box key={device.hwId}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{device.name}</Typography>
                    <Stack spacing={0.5} sx={{ pl: 1 }}>
                      {unbound.map((m) => (
                        <Button
                          key={m.valueName}
                          variant={
                            selectedMeasurement?.hwId === device.hwId &&
                            selectedMeasurement?.valueName === m.valueName
                              ? "contained"
                              : "outlined"
                          }
                          size="small"
                          onClick={() => handleMeasurementClick(device.hwId, m.valueName, device.name, m.name)}
                          sx={{ justifyContent: "flex-start", textTransform: "none" }}
                        >
                          {m.name} — {m.valueName}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {/* List of Custom Alarms */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1.5, pb: 0.5, borderBottom: "1px solid #333" }}>
              {t("Delete Custom Alarms")}
            </Typography>
            <Stack spacing={2}>
              {alarmsLimits?.entries
                ?.filter((alarm) => alarm.customAlarm)
                ?.map((alarm) => (
                  <Box key={`${alarm.hwId}|${alarm.valueName}|${alarm.alarmName}`} sx={{ p: 1.5, border: "1px solid #444", borderRadius: 1, bgcolor: "#1e1e1e" }}>
                     <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="subtitle2">
                            {alarm.deviceName} {alarm.hwId} - {alarm.valueName}
                          </Typography>
                           <Typography variant="caption" color="text.secondary">
                             {t("Limits")}: Low alarm: {alarm.lowAlarm ?? "-"}, Low warning: {alarm.lowWarning ?? "-"}, High warning: {alarm.highWarning ?? "-"}, High alarm: {alarm.highAlarm ?? "-"}
                           </Typography>
                        </Box>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => handleDelete(alarm)}
                        >
                          {t("Delete")}
                        </Button>
                     </Box>
                  </Box>
                ))}
                {(!alarmsLimits?.entries?.some((a) => a.customAlarm)) && (
                   <Typography variant="body2" color="text.secondary">{t("No custom alarms found.")}</Typography>
                )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Divider: drag to resize the list/form split */}
      <Box
        onMouseDown={handleDividerMouseDown}
        sx={{
          flex: "0 0 6px",
          cursor: "col-resize",
          bgcolor: "divider",
          "&:hover": { bgcolor: "primary.main" },
        }}
      />

      {/* Right: create alarm form */}
      <Box
        data-right-panel
        sx={{
          flex: `0 0 ${rightPanelWidth}px`,
          borderLeft: "1px solid",
          borderColor: "divider",
          p: 2,
          overflow: "auto",
          // display: "flex",
          flexDirection: "column",
        }}
      >
        {!selectedMeasurement ? (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: "center", fontSize: "14px" }}>
            {t("Select a measurement to create an alarm")}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="h6">{t("New Custom Alarm")}</Typography>

            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{t("Device")}</Typography>
              <Typography variant="body2">
                {selectedMeasurement.deviceName} ({selectedMeasurement.hwId})
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{t("Measurement")}</Typography>
              <Typography variant="body2">{selectedMeasurement.measurementName}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {t("Alarm Name")}
              </Typography>
              <Box
                component="input"
                type="text"
                value={formValues.alarmName}
                onChange={(e) => handleFieldChange("alarmName", e.target.value)}
                sx={inputSx}
                placeholder={t("Enter a name for the alarm")}
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {t("Category")}
              </Typography>
              <Box
                component="select"
                value={formValues.category}
                onChange={(e) => handleFieldChange("category", e.target.value)}
                sx={inputSx}
              >
                <option value="" disabled>{t("Select a category")}</option>
                <option value="Device">{t("Device")}</option>
                <option value="SampleCondition">{t("SampleCondition")}</option>
              </Box>
              <Collapse in={Boolean(formValues.category)} unmountOnExit>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.75,
                    p: "6px 10px",
                    borderRadius: "4px",
                    bgcolor: "rgba(255, 214, 0, 0.12)",
                    border: "1px solid rgba(255, 214, 0, 0.4)",
                    color: "#ffd600",
                  }}
                >
                  {t(CATEGORY_INFO[formValues.category])}
                </Typography>
              </Collapse>
            </Box>

            {LIMIT_FIELDS.map(({ key, label, placeholder }) => (
              <Box key={key}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {t(label)}
                </Typography>
                <Box
                  component="input"
                  type="text"
                  value={formValues[key]}
                  onChange={(e) => handleLimitChange(key, e.target.value)}
                  sx={inputSx}
                  placeholder={t(placeholder)}
                />
                <Collapse in={Boolean(formValues[key])} unmountOnExit>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, mb: 0.5 }}>
                    {t("Description")}
                  </Typography>
                  <Box
                    component="textarea"
                    rows={2}
                    value={formValues[`${key}Desc`]}
                    onChange={(e) => handleDescChange(key, e.target.value)}
                    sx={{ ...inputSx, resize: "vertical", fontFamily: "inherit" }}
                  />
                </Collapse>
              </Box>
            ))}

            {errorMsg && (
              <Typography color="error" variant="body2">{errorMsg}</Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={saveStatus === "saving"}
                onClick={handleSave}
                fullWidth
              >
                {saveStatus === "saving" ? t("Saving...") : t("Save alarm")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setSelectedMeasurement(null)}
                fullWidth
              >
                {t("Cancel")}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default CreateDeleteAlarms;

